import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import TPSLModal from "./TPSLModal";
import { toJalaliDateTime } from "../../../helper/toJalaliDateTime";
import { useNavigate, useParams } from "react-router-dom";
import SVG from "../../../Components/SVG";
import HistoryGrid from "./OnlyMobile/HistoryGrid";
import HistoryList from "./OnlyMobile/HistoryList";
import OrdersGrid from "./OnlyMobile/OrdersGrid";
import OrdersList from "./OnlyMobile/OrdersList";
import PositionsGrid from "./OnlyMobile/PositionsGrid";
import PositionsList from "./OnlyMobile/PositionsList";
import {
  findSymbolData,
  formatTradeSymbol,
  formatTrimmedNumber,
  inferSymbolPricePrecision,
  normalizeSymbolCode,
  resolveSideMarketPrice,
  resolveTradeEntryPrice,
} from "../tradeUtils";
import { API_BASE_URL } from "../../../config/api";

export default function OrderList({
  accountData,
  marketData,
  symbol,
  className = "",
}) {
  const navigate = useNavigate();
  const { accountId: accountCode } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [onlyThisSymbol, setOnlyThisSymbol] = useState(false);
  const [isTPSLModalOpen, setIsTPSLModalOpen] = useState(false);
  const [tp, setTp] = useState(null);
  const [sl, setSl] = useState(null);
  const [ord, setOrd] = useState(null);
  const [tpSlType, setTpSlType] = useState(null);
  const [tpSlMaxDecimals, setTpSlMaxDecimals] = useState(4);
  const [tpSlEstimateSource, setTpSlEstimateSource] = useState(null);
  const [closedTrades, setClosedTrades] = useState([]);
  const [isTradeHistoryLoading, setIsTradeHistoryLoading] = useState(false);
  const [cancelingOrderId, setCancelingOrderId] = useState(null);
  const [closingPositionId, setClosingPositionId] = useState(null);
  const tabsRef = useRef(null);
  const tabRefs = useRef([]);
  const [tabIndicator, setTabIndicator] = useState({
    left: 0,
    width: 0,
    ready: false,
  });

  useLayoutEffect(() => {
    const tabs = tabsRef.current;
    const activeButton = tabRefs.current[activeTab];
    if (!tabs || !activeButton) return undefined;

    const updateIndicator = () => {
      const nextLeft = activeButton.offsetLeft;
      const nextWidth = activeButton.offsetWidth;

      setTabIndicator((current) => {
        if (
          current.ready &&
          current.left === nextLeft &&
          current.width === nextWidth
        ) {
          return current;
        }

        return { left: nextLeft, width: nextWidth, ready: true };
      });
    };

    updateIndicator();

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updateIndicator);

    resizeObserver?.observe(tabs);
    tabRefs.current.filter(Boolean).forEach((tab) => {
      resizeObserver?.observe(tab);
    });
    window.addEventListener("resize", updateIndicator);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateIndicator);
    };
  }, [activeTab]);

  const formatSymbol = (value) => formatTradeSymbol(value);
  const formatPrice = (value, fallback = "ندارد") =>
    formatTrimmedNumber(value, {
      useGrouping: true,
      fallback,
      maxFractionDigits: 18,
    });
  const toNullableNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const formatOptionalPrice = (value, fallback = "ندارد") => {
    const parsed = toNullableNumber(value);
    return parsed === null ? fallback : `$${formatPrice(parsed, fallback)}`;
  };
  const formatPersianOrderType = (value) => {
    const labels = {
      limit: "لیمیت",
      market: "مارکت",
      stop_limit: "استاپ لیمیت",
      stop_market: "استاپ مارکت",
    };

    return labels[String(value ?? "").toLowerCase()] ?? "ندارد";
  };
  const formatPersianSide = (value) => {
    const normalized = String(value ?? "").toLowerCase();
    if (normalized === "long" || normalized === "buy") return "لانگ";
    if (normalized === "short" || normalized === "sell") return "شورت";
    return "ندارد";
  };
  const formatPnl = (value) => {
    const pnl = Number(value);
    if (!Number.isFinite(pnl)) return "ندارد";
    return pnl >= 0
      ? `$${Number(pnl.toFixed(2)).toLocaleString()}`
      : `-$${Number(Math.abs(pnl).toFixed(2)).toLocaleString()}`;
  };

  const openPositions = useMemo(
    () =>
      Array.isArray(accountData?.positions?.open)
        ? accountData.positions.open
        : [],
    [accountData],
  );
  const openOrders = useMemo(
    () =>
      Array.isArray(accountData?.orders?.open)
        ? accountData.orders.open
        : [],
    [accountData],
  );
  const normalizedSymbol = normalizeSymbolCode(symbol);

  const filteredOpenPositions = useMemo(
    () =>
      onlyThisSymbol
        ? openPositions.filter(
            (item) => normalizeSymbolCode(item?.symbol) === normalizedSymbol,
          )
        : openPositions,
    [normalizedSymbol, onlyThisSymbol, openPositions],
  );

  const filteredOpenOrders = useMemo(
    () =>
      onlyThisSymbol
        ? openOrders.filter(
            (item) => normalizeSymbolCode(item?.symbol) === normalizedSymbol,
          )
        : openOrders,
    [normalizedSymbol, onlyThisSymbol, openOrders],
  );

  const filteredClosedTrades = useMemo(
    () =>
      onlyThisSymbol
        ? closedTrades.filter(
            (item) => normalizeSymbolCode(item?.symbol) === normalizedSymbol,
          )
        : closedTrades,
    [closedTrades, normalizedSymbol, onlyThisSymbol],
  );

  const structuralSignature = useMemo(() => {
    const orderIds = openOrders.map((item) => item?.id).join("|");
    const positionIds = openPositions.map((item) => item?.id).join("|");
    return `${orderIds}::${positionIds}`;
  }, [openOrders, openPositions]);
  const tpSlEstimateContext = useMemo(() => {
    if (!tpSlEstimateSource) return null;

    const symbolData = findSymbolData(
      marketData?.symbols,
      tpSlEstimateSource.rawSymbol,
    );
    const currentMarketPrice = resolveSideMarketPrice(
      symbolData,
      tpSlEstimateSource.rawSide,
    );

    if (tpSlEstimateSource.type === "position") {
      return {
        side: tpSlEstimateSource.rawSide,
        marginUsdt: tpSlEstimateSource.marginUsdt,
        leverage: accountData?.account?.leverage ?? 0,
        entryPrice: tpSlEstimateSource.entryPriceValue,
        marketPrice: currentMarketPrice,
      };
    }

    return {
      side: tpSlEstimateSource.rawSide,
      marginUsdt: tpSlEstimateSource.marginUsdt,
      leverage: accountData?.account?.leverage ?? 0,
      entryPrice: resolveTradeEntryPrice({
        orderType: tpSlEstimateSource.rawOrderType,
        marketPrice: currentMarketPrice,
        limitPrice: tpSlEstimateSource.limitPriceValue,
        stopPrice: tpSlEstimateSource.stopPriceValue,
      }),
      marketPrice: currentMarketPrice,
    };
  }, [accountData, marketData, tpSlEstimateSource]);

  useEffect(() => {
    setClosedTrades([]);
  }, [accountCode]);

  useEffect(() => {
    if (activeTab !== 3 || !accountCode) return;

    let isCancelled = false;

    const fetchTradeHistory = async () => {
      setIsTradeHistoryLoading(true);

      try {
        let response = await fetch(
          `${API_BASE_URL}trading/accounts/${accountCode}/trade-history`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
          },
        );

        if (response.status === 401) {
          const refreshResponse = await fetch(
            `${API_BASE_URL}auth/trader/jwt/refresh/`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                refresh: localStorage.getItem("refresh"),
              }),
            },
          );

          if (!refreshResponse.ok) {
            localStorage.removeItem("access");
            localStorage.removeItem("authCredentials");
            localStorage.removeItem("refresh");
            localStorage.removeItem("role");
            localStorage.removeItem("email");
            navigate("/signin");
            return;
          }

          const refreshData = await refreshResponse.json();
          localStorage.setItem("access", refreshData.access);

          response = await fetch(
            `${API_BASE_URL}trading/accounts/${accountCode}/trade-history`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("access")}`,
              },
            },
          );
        }

        if (!response.ok) {
          throw new Error(`https error! Status: ${response.status}`);
        }

        const data = await response.json();
        if (isCancelled) return;
        setClosedTrades(Array.isArray(data?.trades) ? data.trades : []);
      } catch (error) {
        if (!isCancelled) {
          console.error("Error fetching trade history:", error);
          setClosedTrades([]);
        }
      } finally {
        if (!isCancelled) {
          setIsTradeHistoryLoading(false);
        }
      }
    };

    fetchTradeHistory();

    return () => {
      isCancelled = true;
    };
  }, [accountCode, activeTab, navigate, structuralSignature]);

  const openTPSLModal = (row, type) => {
    const symbolData = findSymbolData(marketData?.symbols, row.rawSymbol);

    setTpSlType(type);
    setOrd(row.id);
    setTp(row.tp);
    setSl(row.sl);
    setTpSlMaxDecimals(inferSymbolPricePrecision(symbolData, 4));
    setTpSlEstimateSource({ ...row, type });
    setIsTPSLModalOpen(true);
  };

  const handleCancel = async (posId) => {
    if (cancelingOrderId) return;

    setCancelingOrderId(String(posId));
    try {
      let response = await fetch(
        `${API_BASE_URL}trading/cancel-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
          body: JSON.stringify({
            account_id: accountCode,
            order_id: posId,
          }),
        },
      );

      if (response.status === 401) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}auth/trader/jwt/refresh/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refresh"),
            }),
          },
        );

        if (!refreshResponse.ok) {
          localStorage.removeItem("access");
          localStorage.removeItem("authCredentials");
          localStorage.removeItem("refresh");
          localStorage.removeItem("role");
          localStorage.removeItem("email");
          navigate("/signin");
          return;
        }

        const refreshData = await refreshResponse.json();
        localStorage.setItem("access", refreshData.access);

        response = await fetch(
          `${API_BASE_URL}trading/cancel-order`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
            body: JSON.stringify({
              account_id: accountCode,
              order_id: posId,
            }),
          },
        );
      }

      if (!response.ok) {
        throw new Error(`https error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Account Data:", data);
    } catch (error) {
      console.error("Error fetching account data:", error);
    } finally {
      setCancelingOrderId(null);
    }
  };

  const handleClose = async (posId) => {
    if (closingPositionId) return;

    setClosingPositionId(String(posId));
    try {
      let response = await fetch(
        `${API_BASE_URL}trading/close-position`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
          body: JSON.stringify({
            account_id: accountCode,
            position_id: posId,
          }),
        },
      );

      if (response.status === 401) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}auth/trader/jwt/refresh/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refresh"),
            }),
          },
        );

        if (!refreshResponse.ok) {
          localStorage.removeItem("access");
          localStorage.removeItem("authCredentials");
          localStorage.removeItem("refresh");
          localStorage.removeItem("role");
          localStorage.removeItem("email");
          navigate("/signin");
          return;
        }

        const refreshData = await refreshResponse.json();
        localStorage.setItem("access", refreshData.access);

        response = await fetch(
          `${API_BASE_URL}trading/close-position`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
            body: JSON.stringify({
              account_id: accountCode,
              position_id: posId,
            }),
          },
        );
      }

      if (!response.ok) {
        throw new Error(`https error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Account Data:", data);
    } catch (error) {
      console.error("Error fetching account data:", error);
    } finally {
      setClosingPositionId(null);
    }
  };

  const positionRows = filteredOpenPositions.map((pos) => ({
    id: pos.id,
    rawSymbol: pos.symbol,
    rawSide: pos.side,
    tp: toNullableNumber(pos.tp),
    sl: toNullableNumber(pos.sl),
    marginUsdt: toNullableNumber(pos.margin_usdt),
    entryPriceValue: toNullableNumber(pos.entry_price),
    date: toJalaliDateTime(pos.opened_at),
    symbol: formatSymbol(pos.symbol),
    type: formatPersianOrderType(pos.order_type),
    side: formatPersianSide(pos.side),
    entryPrice: formatOptionalPrice(pos.entry_price),
    totalCost: `$${Number(pos.margin_usdt).toLocaleString()}`,
    takeProfit: formatOptionalPrice(pos.tp),
    stopLoss: formatOptionalPrice(pos.sl),
    pnl: formatPnl(pos.unrealized_pnl),
    numberPnl: Number(pos.unrealized_pnl),
    operation: "true",
  }));

  const openOrderRows = filteredOpenOrders.map((ord) => ({
    id: `${ord.id}`,
    rawSymbol: ord.symbol,
    rawSide: ord.side,
    rawOrderType: ord.type ?? ord.order_type,
    tp: toNullableNumber(ord.tp),
    sl: toNullableNumber(ord.sl),
    marginUsdt: toNullableNumber(ord.margin_usdt),
    stopPriceValue: toNullableNumber(ord.stop_price),
    limitPriceValue: toNullableNumber(ord.limit_price),
    date: toJalaliDateTime(ord.created_at),
    symbol: formatSymbol(ord.symbol),
    type: formatPersianOrderType(ord.type ?? ord.order_type),
    side: formatPersianSide(ord.side),
    stopPrice: formatOptionalPrice(ord.stop_price),
    orderPrice: formatOptionalPrice(ord.limit_price),
    totalCost: `$${Number(ord.margin_usdt).toLocaleString()}`,
    takeProfit: formatOptionalPrice(ord.tp),
    stopLoss: formatOptionalPrice(ord.sl),
    operation: "true",
  }));

  const closedTradeRows = filteredClosedTrades.map((trade) => ({
    id: trade.id,
    openDate: toJalaliDateTime(trade.opened_at),
    closeDate: toJalaliDateTime(trade.closed_at),
    symbol: formatSymbol(trade.symbol),
    type: formatPersianOrderType(trade.order_type),
    side: formatPersianSide(trade.side),
    entryPrice: formatOptionalPrice(trade.entry_price),
    exitPrice: formatOptionalPrice(trade.exit_price),
    totalCost: `$${Number(trade.margin_usdt).toLocaleString()}`,
    pnl: formatPnl(trade.profit_loss),
    numberPnl: Number(trade.profit_loss),
  }));

  return (
    <div
      className={`w-full min-h-0 overflow-hidden rounded-2xl py-2.5 px-3 mb:px-5 flex flex-col bg-zi-bg2-light dark:bg-zi-bg2-dark ${className}`}
    >
      <div className="w-full shrink-0 flex items-center justify-between gap-14 min-w-0 overflow-x-auto text-zi-text-light dark:text-zi-text-dark border-b border-zi-border-dark/50 dark:border-b-0">
        <div
          ref={tabsRef}
          className="relative w-full flex items-center justify-between mb:justify-normal gap-2.5 mb:gap-7 text-sm"
        >
          <span
            aria-hidden="true"
            className={`absolute bottom-0 left-0 h-0.5 rounded-t-full bg-zi-border-dark dark:bg-zi-border-light transition-[transform,width] duration-300 ease-out ${
              tabIndicator.ready ? "opacity-100" : "opacity-0"
            }`}
            style={{
              width: `${tabIndicator.width}px`,
              transform: `translateX(${tabIndicator.left}px)`,
            }}
          />
          <button
            ref={(element) => {
              tabRefs.current[0] = element;
            }}
            type="button"
            onClick={() => setActiveTab(0)}
            className={`pb-2.5 text-nowrap transition-colors duration-300 ${
              activeTab === 0
                ? "text-zi-text-light dark:text-zi-text-dark"
                : "text-zi-text-light/50 dark:text-zi-text-dark/50 hocus:text-zi-text-light hocus:dark:text-zi-text-dark"
            }`}
          >
            پوزیشن‌ها ({filteredOpenPositions.length})
          </button>
          <button
            ref={(element) => {
              tabRefs.current[1] = element;
            }}
            type="button"
            onClick={() => setActiveTab(1)}
            className={`pb-2.5 text-nowrap transition-colors duration-300 ${
              activeTab === 1
                ? "text-zi-text-light dark:text-zi-text-dark"
                : "text-zi-text-light/50 dark:text-zi-text-dark/50 hocus:text-zi-text-light hocus:dark:text-zi-text-dark"
            }`}
          >
            ‌سفارشات باز ({filteredOpenOrders.length})
          </button>
          <button
            ref={(element) => {
              tabRefs.current[3] = element;
            }}
            type="button"
            onClick={() => setActiveTab(3)}
            className={`pb-2.5 text-nowrap transition-colors duration-300 ${
              activeTab === 3
                ? "text-zi-text-light dark:text-zi-text-dark"
                : "text-zi-text-light/50 dark:text-zi-text-dark/50 hocus:text-zi-text-light hocus:dark:text-zi-text-dark"
            }`}
          >
            تاریخچه معاملات
          </button>
        </div>
        <button
          type="button"
          className={`hidden mb:flex text-nowrap items-center gap-2 text-xs pb-2.5 ${onlyThisSymbol ? "text-zi-text-light dark:text-zi-text-dark" : "text-zi-text-light/50 dark:text-zi-text-dark/50"}`}
          onClick={() => setOnlyThisSymbol((prev) => !prev)}
        >
          <span className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded-[4px] border ${onlyThisSymbol ? "border-zi-border-dark dark:border-zi-border-light" : "border-zi-border-dark/50 dark:border-zi-border-light/50"} text-[10px]`}>
            {onlyThisSymbol && (
              <SVG
                name="checkMark"
                classname="size-2 stroke-zi-text-light dark:stroke-zi-text-dark"
              />
            )}
          </span>
          <span>فقط نمایش این نماد</span>
        </button>
      </div>
      <div className="card-divider hidden dark:block w-full h-px shrink-0 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.09)_20%,rgba(255,255,255,0.22)_50%,rgba(255,255,255,0.09)_80%,transparent)]" />
      <button
        type="button"
        className={`flex mb:hidden text-nowrap items-center gap-2 text-xs self-end ${onlyThisSymbol ? "text-zi-text-light dark:text-zi-text-dark" : "text-zi-text-light/50 dark:text-zi-text-dark/50"} mt-2.5`}
        onClick={() => setOnlyThisSymbol((prev) => !prev)}
      >
        <span className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded-[4px] border ${onlyThisSymbol ? "border-zi-border-dark dark:border-zi-border-light" : "border-zi-border-dark/50 dark:border-zi-border-light/50"} text-[10px]`}>
          {onlyThisSymbol && (
            <SVG
              name="checkMark"
              classname="size-2 stroke-zi-text-light dark:stroke-zi-text-dark"
            />
          )}
        </span>
        <span>فقط نمایش این نماد</span>
      </button>
      {activeTab === 0 && (
        <div className="min-h-0 flex-1 pt-2.5 flex flex-col overflow-hidden">
          <PositionsGrid
            rows={positionRows}
            closingPositionId={closingPositionId}
            onEdit={(row) => openTPSLModal(row, "position")}
            onClose={handleClose}
          />
          <PositionsList
            rows={positionRows}
            closingPositionId={closingPositionId}
            onEdit={(row) => openTPSLModal(row, "position")}
            onClose={handleClose}
          />
          {filteredOpenPositions.length === 0 && (
            <div className="w-full flex justify-center items-center flex-col gap-5 pt-5">
              <p className="text-zi-text-light dark:text-zi-text-dark text-sm sm:text-base">
                موردی برای نمایش وجود ندارد
              </p>
              <SVG name="noData" classname="w-24 fill-cyan-500" />
            </div>
          )}
        </div>
      )}
      {activeTab === 1 && (
        <div className="min-h-0 flex-1 pt-2.5 flex flex-col overflow-hidden">
          <OrdersGrid
            rows={openOrderRows}
            cancelingOrderId={cancelingOrderId}
            onEdit={(row) => openTPSLModal(row, "order")}
            onCancel={handleCancel}
          />
          <OrdersList
            rows={openOrderRows}
            cancelingOrderId={cancelingOrderId}
            onEdit={(row) => openTPSLModal(row, "order")}
            onCancel={handleCancel}
          />
          {filteredOpenOrders.length === 0 && (
            <div className="w-full flex justify-center items-center flex-col gap-5 pt-5">
              <p className="text-zi-text-light dark:text-zi-text-dark text-sm sm:text-base">
                موردی برای نمایش وجود ندارد
              </p>
              <SVG name="noData" classname="w-24 fill-cyan-500" />
            </div>
          )}
        </div>
      )}
      {activeTab === 3 && (
        <div className="min-h-0 flex-1 pt-2.5 flex flex-col overflow-hidden">
          <HistoryGrid rows={closedTradeRows} />
          <HistoryList rows={closedTradeRows} />
          {isTradeHistoryLoading && (
            <div className="w-full flex justify-center items-center pt-5">
              <p className="text-zi-text-light dark:text-zi-text-dark">
                در حال بارگذاری
              </p>
            </div>
          )}
          {!isTradeHistoryLoading && filteredClosedTrades.length === 0 && (
            <div className="w-full flex justify-center items-center flex-col gap-5 pt-5">
              <p className="text-zi-text-light dark:text-zi-text-dark text-sm sm:text-base">
                موردی برای نمایش وجود ندارد
              </p>
              <SVG name="noData" classname="w-24 fill-cyan-500" />
            </div>
          )}
        </div>
      )}
      {isTPSLModalOpen && (
        <div
          className="w-full fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur"
          onClick={() => {
            setIsTPSLModalOpen(false);
            setTpSlEstimateSource(null);
          }}
        >
          <div
            className="w-11/12 max-w-sm flex justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <TPSLModal
              tp={tp}
              sl={sl}
              ord={ord}
              tpSlType={tpSlType}
              estimateContext={tpSlEstimateContext}
              maxPriceDecimals={tpSlMaxDecimals}
              onClose={() => {
                setIsTPSLModalOpen(false);
                setTpSlEstimateSource(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
